<template>
  <div class="profile-image-container" :class="{ editable }" @click="handleClick">
    <img
      v-if="currentImageUrl"
      class="profile-image"
      :src="currentImageUrl"
      alt="Foto do Perfil"
      @error="onImageError" />
    <div v-else class="profile-initials">
      {{ initials }}
    </div>
    <div v-if="editable" class="overlay">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
      </svg>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png"
      class="hidden"
      @change="onFileSelected" />
    <div v-if="isUploading" class="overlay uploading">
      <svg class="h-6 w-6 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const DEFAULT_AVATAR_URL = new URL('@/assets/images/user/placeholder_avatar.jpg', import.meta.url).href

const props = withDefaults(
  defineProps<{
    avatarUrl?: string | null
    userName?: string
    editable?: boolean
  }>(),
  {
    avatarUrl: null,
    userName: '',
    editable: false,
  },
)

const emit = defineEmits<{
  'update:avatar': [file: File]
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const isUploading = ref(false)
const hasImageError = ref(false)

const initials = computed(() => {
  if (!props.userName) return '?'
  const parts = props.userName.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return parts[0][0].toUpperCase()
})

const currentImageUrl = computed(() => {
  if (hasImageError.value) return null
  if (props.avatarUrl) return props.avatarUrl
  return null
})

const onImageError = () => {
  hasImageError.value = true
}

const handleClick = () => {
  if (props.editable) {
    fileInput.value?.click()
  }
}

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  isUploading.value = true
  emit('update:avatar', file)
  // Reset input so the same file can be re-selected
  input.value = ''
}

const setUploadComplete = () => {
  isUploading.value = false
  hasImageError.value = false
}

defineExpose({ setUploadComplete })
</script>

<style scoped>
.profile-image-container {
  position: relative;
  display: inline-flex;
  width: 88px;
  height: 88px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.profile-image-container.editable {
  cursor: pointer;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  color: #64748b;
  font-size: 24px;
  font-weight: 700;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.2s;
}

.profile-image-container.editable:hover .overlay {
  opacity: 1;
}

.overlay.uploading {
  opacity: 1;
}

.hidden {
  display: none;
}
</style>
